<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatBRL } from 'dashboard/helper/lais/currency';

const props = defineProps({
  beneficiary: { type: Object, required: true },
});

const { t } = useI18n();
const money = value =>
  formatBRL(value?.amount, {
    fallback: t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY'),
    fractionDigits: 2,
  });
const beneficiaryUser = computed(() => props.beneficiary.usuario || {});
const beneficiaryCompany = computed(() => props.beneficiary.empresa || {});
const beneficiaryIdentity = computed(() =>
  props.beneficiary.tipo === 'empresa'
    ? beneficiaryCompany.value
    : beneficiaryUser.value
);
const isCompany = computed(() => props.beneficiary.tipo === 'empresa');
const documentLabel = computed(() =>
  isCompany.value
    ? t('REAL_ESTATE_FINANCE.TRANSFER.COMPANY_DOCUMENT')
    : t('REAL_ESTATE_FINANCE.TRANSFER.PERSON_DOCUMENT')
);
const documentValue = computed(
  () => beneficiaryIdentity.value.cnpj || beneficiaryIdentity.value.cpf
);
const paymentMethod = computed(() => props.beneficiary.metodo_pagamento || {});
const isPix = computed(() => paymentMethod.value.tipo_pagamento === 'pix');
const allocationSummary = computed(() => [
  {
    label: t('REAL_ESTATE_FINANCE.TRANSFER.REMAINING'),
    value: props.beneficiary.valor_restante,
  },
  {
    label: t('REAL_ESTATE_FINANCE.TRANSFER.TRANSFERRED'),
    value: props.beneficiary.valor_transferido,
  },
  {
    label: t('REAL_ESTATE_FINANCE.TRANSFER.TOTAL'),
    value: props.beneficiary.valor_total || props.beneficiary.valor_confirmado,
  },
]);
const paymentDetails = computed(() =>
  isPix.value
    ? paymentMethod.value.chave_pix
    : [
        paymentMethod.value.banco_id,
        paymentMethod.value.nome_banco,
        paymentMethod.value.agencia,
        paymentMethod.value.numero_conta,
      ]
        .filter(Boolean)
        .join(' · ')
);
</script>

<template>
  <div class="space-y-5">
    <div class="rounded-lais bg-lais-bg-soft p-4">
      <p class="text-xs text-lais-text-muted">
        {{
          isCompany
            ? t('REAL_ESTATE_FINANCE.TRANSFER.COMPANY_NAME')
            : t('REAL_ESTATE_FINANCE.TRANSFER.FULL_NAME')
        }}
      </p>
      <p class="mt-1 font-semibold text-lais-text">
        {{
          beneficiaryIdentity.name ||
          t('REAL_ESTATE_FINANCE.TRANSFER.EMPTY_VALUE')
        }}
      </p>
      <p class="mt-1 text-sm text-lais-text-body">
        {{
          t('REAL_ESTATE_FINANCE.TRANSFER.DOCUMENT_VALUE', {
            label: documentLabel,
            value:
              documentValue || t('REAL_ESTATE_FINANCE.TRANSFER.EMPTY_VALUE'),
          })
        }}
      </p>
    </div>
    <div class="grid gap-3 sm:grid-cols-3">
      <div
        v-for="item in allocationSummary"
        :key="item.label"
        class="rounded-lais border border-lais-border-soft p-3"
      >
        <p class="text-xs text-lais-text-muted">{{ item.label }}</p>
        <p class="mt-1 font-semibold text-lais-text">
          {{ money(item.value) }}
        </p>
      </div>
    </div>
    <div class="rounded-lais border border-lais-border-soft p-4 text-sm">
      <p class="font-semibold text-lais-text">
        {{ t('REAL_ESTATE_FINANCE.TRANSFER.PAYMENT_METHOD') }}
      </p>
      <div v-if="paymentDetails" class="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <p class="text-xs text-lais-text-muted">
            {{ t('REAL_ESTATE_FINANCE.TRANSFER.METHOD') }}
          </p>
          <p class="mt-1 text-lais-text-body">
            {{
              isPix
                ? t('REAL_ESTATE_FINANCE.TRANSFER.PIX')
                : t('REAL_ESTATE_FINANCE.TRANSFER.BANK')
            }}
          </p>
        </div>
        <div v-if="isPix">
          <p class="text-xs text-lais-text-muted">
            {{ t('REAL_ESTATE_FINANCE.TRANSFER.PIX_KEY') }}
          </p>
          <p class="mt-1 break-words text-lais-text-body">
            {{ paymentMethod.chave_pix }}
          </p>
        </div>
        <template v-else>
          <div>
            <p class="text-xs text-lais-text-muted">
              {{ t('REAL_ESTATE_FINANCE.TRANSFER.BANK_CODE') }}
            </p>
            <p class="mt-1 text-lais-text-body">
              {{
                paymentMethod.banco_id ||
                t('REAL_ESTATE_FINANCE.TRANSFER.EMPTY_VALUE')
              }}
            </p>
          </div>
          <div>
            <p class="text-xs text-lais-text-muted">
              {{ t('REAL_ESTATE_FINANCE.TRANSFER.BANK_NAME') }}
            </p>
            <p class="mt-1 text-lais-text-body">
              {{
                paymentMethod.nome_banco ||
                t('REAL_ESTATE_FINANCE.TRANSFER.EMPTY_VALUE')
              }}
            </p>
          </div>
          <div>
            <p class="text-xs text-lais-text-muted">
              {{ t('REAL_ESTATE_FINANCE.TRANSFER.BRANCH') }}
            </p>
            <p class="mt-1 text-lais-text-body">
              {{
                paymentMethod.agencia ||
                t('REAL_ESTATE_FINANCE.TRANSFER.EMPTY_VALUE')
              }}
            </p>
          </div>
          <div>
            <p class="text-xs text-lais-text-muted">
              {{ t('REAL_ESTATE_FINANCE.TRANSFER.ACCOUNT') }}
            </p>
            <p class="mt-1 text-lais-text-body">
              {{
                paymentMethod.numero_conta ||
                t('REAL_ESTATE_FINANCE.TRANSFER.EMPTY_VALUE')
              }}
            </p>
          </div>
        </template>
      </div>
      <p v-else class="mt-1 text-lais-text-body">
        {{ t('REAL_ESTATE_FINANCE.TRANSFER.NO_PAYMENT_METHOD') }}
      </p>
    </div>
  </div>
</template>
